import React from 'react';
import { i18n } from 'config/locales';

import { HistoryEducationDiplomaModel } from 'forms/sections/history/education/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { setAttribute } from 'components/Form/shared/setAttribute';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import DateControl from 'components/Form/DateControl';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';

interface Props
  extends Partial<HistoryEducationDiplomaModel>,
  ValidationElementProps,
  AccordionItemProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  formType?: string
}

export default class DiplomaItem extends ValidationElement<Props> {
  static defaultProps = {
    Diploma: {},
    DiplomaOther: {},
    Date: {},
    Dates: {},
    // TODO: Remove
    onUpdate: () => { },
    // TODO: Remove
    onError: (value, arr) => arr,
  };

  update = (queue) => {
    this.props.onUpdate({
      Diploma: this.props.Diploma,
      DiplomaOther: this.props.DiplomaOther,
      Date: this.props.Date,
      Dates: this.props.Dates, // used in Date validation to ensure Date falls within Dates
      ...queue,
    });
  };

  updateDiploma = (values: HistoryEducationDiplomaModel['Diploma']) => {
    this.update({
      Diploma: values,
    });
  };

  updateDiplomaOther = (values: HistoryEducationDiplomaModel['DiplomaOther']) => {
    this.update({
      DiplomaOther: values,
    });
  };

  updateDate = (values: HistoryEducationDiplomaModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  render() {
    const attribute = setAttribute(this.props.attribute, `items[${this.props.$index}].Item`);

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div>
        <Field
          title={i18n.t('history.pvq.education.heading.diploma')}
          titleSize="h4"
          help="history.pvq.education.help.diploma"
          adjustFor="big-buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Diploma`}
        >
          <RadioGroup
            className="diploma option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Diploma || {}).value}
            attribute={`${attribute}.Diploma`}
          >
            <Radio
              name="diploma-highschool"
              className="diploma-highschool"
              label={i18n.t('history.pvq.education.label.diploma.highschool')}
              value="High School diploma"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-associate"
              className="diploma-associate"
              label={i18n.t('history.pvq.education.label.diploma.associate')}
              value="Associate's degree"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-bachelor"
              className="diploma-bachelor"
              label={i18n.t('history.pvq.education.label.diploma.bachelor')}
              value="Bachelor's degree"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-master"
              className="diploma-master"
              label={i18n.t('history.pvq.education.label.diploma.master')}
              value="Master's degree"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-doctorate"
              className="diploma-doctorate"
              label={i18n.t('history.education.label.diploma.doctorate')}
              value="Doctoral degree"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-professional"
              className="diploma-professional"
              label={i18n.t('history.pvq.education.label.diploma.professional')}
              value="Professional degree"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-other"
              className="diploma-other"
              label={i18n.t('history.pvq.education.label.diploma.other')}
              value="Other"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
          </RadioGroup>
          <Show when={(this.props.Diploma || {}).value === 'Other'}>
            <Field
              title={i18n.t('history.pvq.education.label.diploma.otherDiploma')}
              titleSize="label"
              adjustFor="text"
              attribute={`${attribute}.DiplomaOther`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Text
                name="DiplomaOther"
                {...this.props.DiplomaOther}
                className="other"
                maxlength="100"
                onUpdate={this.updateDiplomaOther}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.DiplomaOther`}
              />
            </Field>
          </Show>
        </Field>

        <Field
          title={i18n.t('history.pvq.education.heading.date')}
          titleSize="h4"
          help="history.pvq.education.help.date"
          adjustFor="label"
          shrink
          attribute={`${attribute}.Date`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            attribute={`${attribute}.Date`}
            name="Date"
            {...this.props.Date}
            className="date-awarded"
            minDateEqualTo
            maxDate={{ year: new Date().getFullYear() }}
            hideDay
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
      </div>

    ) : (
      <div>
        <Field
          title={i18n.t('history.education.heading.diploma')}
          titleSize="h4"
          help="history.education.help.diploma"
          adjustFor="big-buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Diploma`}
        >
          <RadioGroup
            className="diploma option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Diploma || {}).value}
            attribute={`${attribute}.Diploma`}
          >
            <Radio
              name="diploma-highschool"
              className="diploma-highschool"
              label={i18n.t('history.education.label.diploma.highschool')}
              value="High School Diploma"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-associate"
              className="diploma-associate"
              label={i18n.t('history.education.label.diploma.associate')}
              value="Associate"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-bachelor"
              className="diploma-bachelor"
              label={i18n.t('history.education.label.diploma.bachelor')}
              value="Bachelor"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-master"
              className="diploma-master"
              label={i18n.t('history.education.label.diploma.master')}
              value="Master"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-doctorate"
              className="diploma-doctorate"
              label={i18n.t('history.education.label.diploma.doctorate')}
              value="Doctorate"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-professional"
              className="diploma-professional"
              label={i18n.t('history.education.label.diploma.professional')}
              value="Professional"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
            <Radio
              name="diploma-other"
              className="diploma-other"
              label={i18n.t('history.education.label.diploma.other')}
              value="Other"
              onUpdate={this.updateDiploma}
              onError={this.props.onError}
            />
          </RadioGroup>
          <Show when={(this.props.Diploma || {}).value === 'Other'}>
            <Field
              title={i18n.t('history.education.label.diploma.otherDiploma')}
              titleSize="label"
              adjustFor="text"
              attribute={`${attribute}.DiplomaOther`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Text
                name="DiplomaOther"
                {...this.props.DiplomaOther}
                className="other"
                maxlength="100"
                onUpdate={this.updateDiplomaOther}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.DiplomaOther`}
              />
            </Field>
          </Show>
        </Field>

        <Field
          title={i18n.t('history.education.heading.date')}
          titleSize="h4"
          help="history.education.help.date"
          adjustFor="label"
          shrink
          attribute={`${attribute}.Date`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            attribute={`${attribute}.Date`}
            name="Date"
            {...this.props.Date}
            className="date-awarded"
            minDateEqualTo
            maxDate={{ year: new Date().getFullYear() }}
            hideDay
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
      </div>
    );
  }
}
